import { useState, useRef, useCallback, useEffect } from 'react';

export function useAnnotation(
    galleryTab,
    filteredGallery,
    selectedGalleryIndex,
    gallery,
    setGallery,
) {
    const annotateCanvasRef = useRef(null);
    const annotateCtxRef = useRef(null);
    const [annotateDrawing, setAnnotateDrawing] = useState(false);
    const annotateDrawingRef = useRef(false);
    const [annotateBrushColor, setAnnotateBrushColor] = useState('#ff0000');
    const [annotateBrushSize, setAnnotateBrushSize] = useState(3);
    const [annotateEraser, setAnnotateEraser] = useState(false);
    const [annotateZoomMode, setAnnotateZoomMode] = useState(false);
    const [drawTool, setDrawTool] = useState('pen');
    const annotateHistoryRef = useRef([]);
    const annotateHistoryIdxRef = useRef(-1);
    const annotateOriginalRef = useRef(null);
    const annotateLastPt = useRef(null);
    const shapeStartRef = useRef(null);
    const shapeSnapshotRef = useRef(null);
    const [annotateNoteMode, setAnnotateNoteMode] = useState(false);
    const [annotateNotes, setAnnotateNotes] = useState([]);
    const [annotateEditingNote, setAnnotateEditingNote] = useState(null);
    const annotateDraggingNote = useRef(null);
    const annotateContainerRef = useRef(null);
    const [annotateCanvasRect, setAnnotateCanvasRect] = useState(null);

    // Bezier curve tool
    const [annotateCurveMode, setAnnotateCurveMode] = useState(false);
    const [annotateCurvePoints, setAnnotateCurvePoints] = useState([]);
    const [annotateCurveClosed, setAnnotateCurveClosed] = useState(false);
    const annotateCurveDragging = useRef(null);
    const annotateCurveDragUsed = useRef(false);

    const drawToolRef = useRef(drawTool);
    drawToolRef.current = drawTool;

    const initCanvas = useCallback((imgUrl) => {
        const canvas = annotateCanvasRef.current;
        if (!canvas) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.drawImage(img, 0, 0);
            annotateCtxRef.current = ctx;
            const offscreen = document.createElement('canvas');
            offscreen.width = img.width;
            offscreen.height = img.height;
            offscreen.getContext('2d').drawImage(img, 0, 0);
            annotateOriginalRef.current = offscreen;
            const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
            annotateHistoryRef.current = [data];
            annotateHistoryIdxRef.current = 0;
        };
        img.src = imgUrl;
    }, []);

    const pushHistory = useCallback(() => {
        const ctx = annotateCtxRef.current;
        const canvas = annotateCanvasRef.current;
        if (!ctx || !canvas) return;
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const idx = annotateHistoryIdxRef.current + 1;
        annotateHistoryRef.current = annotateHistoryRef.current.slice(0, idx);
        annotateHistoryRef.current.push(data);
        annotateHistoryIdxRef.current = idx;
    }, []);

    const undo = useCallback(() => {
        const ctx = annotateCtxRef.current;
        const canvas = annotateCanvasRef.current;
        if (!ctx || !canvas) return;
        if (annotateHistoryIdxRef.current > 0) {
            annotateHistoryIdxRef.current--;
            ctx.putImageData(annotateHistoryRef.current[annotateHistoryIdxRef.current], 0, 0);
        }
    }, []);

    const addNote = useCallback((e) => {
        if (!annotateNoteMode) return;
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width;
        const py = (e.clientY - rect.top) / rect.height;
        if (px < 0 || px > 1 || py < 0 || py > 1) return;
        const tx = Math.min(px + 0.15, 0.85);
        const ty = Math.max(py - 0.1, 0.02);
        setAnnotateNotes(prev => [...prev, { px, py, tx, ty, text: '' }]);
        setTimeout(() => setAnnotateEditingNote(annotateNotes.length), 0);
    }, [annotateNoteMode, annotateNotes.length]);

    const drawArrowhead = useCallback((ctx, fromX, fromY, toX, toY, size) => {
        const angle = Math.atan2(toY - fromY, toX - fromX);
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(toX - size * Math.cos(angle - Math.PI / 6), toY - size * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(toX - size * Math.cos(angle + Math.PI / 6), toY - size * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
    }, []);

    const drawShape = useCallback((ctx, tool, x1, y1, x2, y2, lineWidth) => {
        ctx.beginPath();
        switch (tool) {
            case 'line':
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
                break;
            case 'arrow': {
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
                const headSize = Math.max(lineWidth * 3, 12);
                ctx.fillStyle = ctx.strokeStyle;
                drawArrowhead(ctx, x1, y1, x2, y2, headSize);
                break;
            }
            case 'rect':
                ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
                break;
            case 'circle': {
                const cx = (x1 + x2) / 2;
                const cy = (y1 + y2) / 2;
                const rx = Math.abs(x2 - x1) / 2;
                const ry = Math.abs(y2 - y1) / 2;
                ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
                ctx.stroke();
                break;
            }
            default: break;
        }
    }, [drawArrowhead]);

    const startDraw = useCallback((e) => {
        if (annotateZoomMode || annotateNoteMode) return;
        const ctx = annotateCtxRef.current;
        const canvas = annotateCanvasRef.current;
        if (!ctx || !canvas) return;
        annotateDrawingRef.current = true;
        setAnnotateDrawing(true);
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;
        annotateLastPt.current = { x, y };

        const tool = drawToolRef.current;

        if (tool !== 'pen') {
            shapeStartRef.current = { x, y };
            shapeSnapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
        }

        ctx.lineWidth = annotateBrushSize * scaleX;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        if (annotateEraser && annotateOriginalRef.current) {
            ctx.globalCompositeOperation = 'source-over';
            const pattern = ctx.createPattern(annotateOriginalRef.current, 'no-repeat');
            ctx.strokeStyle = pattern || 'rgba(255,255,255,1)';
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = annotateBrushColor;
        }

        if (tool === 'pen') {
            ctx.beginPath();
            ctx.moveTo(x, y);
        }
    }, [annotateBrushColor, annotateBrushSize, annotateEraser, annotateZoomMode, annotateNoteMode]);

    const draw = useCallback((e) => {
        if (!annotateDrawingRef.current) return;
        const ctx = annotateCtxRef.current;
        const canvas = annotateCanvasRef.current;
        const last = annotateLastPt.current;
        if (!ctx || !canvas || !last) return;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        const tool = drawToolRef.current;

        if (tool === 'pen') {
            const mx = (last.x + x) / 2;
            const my = (last.y + y) / 2;
            ctx.quadraticCurveTo(last.x, last.y, mx, my);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(mx, my);
            annotateLastPt.current = { x, y };
        } else {
            const start = shapeStartRef.current;
            const snapshot = shapeSnapshotRef.current;
            if (!start || !snapshot) return;
            ctx.putImageData(snapshot, 0, 0);
            ctx.lineWidth = annotateBrushSize * scaleX;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = annotateBrushColor;
            drawShape(ctx, tool, start.x, start.y, x, y, ctx.lineWidth);
        }
    }, [annotateBrushColor, annotateBrushSize, drawShape]);

    const endDraw = useCallback(() => {
        if (!annotateDrawingRef.current) return;
        annotateDrawingRef.current = false;
        setAnnotateDrawing(false);
        annotateLastPt.current = null;
        shapeStartRef.current = null;
        shapeSnapshotRef.current = null;
        const ctx = annotateCtxRef.current;
        if (ctx) ctx.globalCompositeOperation = 'source-over';
        pushHistory();
    }, [pushHistory]);

    const generateCurvePath = useCallback((points, w, h, closed) => {
        if (points.length < 2) return '';
        const pts = points.map(p => ({ x: p.x * w, y: p.y * h }));
        const n = pts.length;
        if (n === 2 && !closed) {
            return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
        }
        const segments = closed ? n : n - 1;
        let d = `M ${pts[0].x} ${pts[0].y}`;
        for (let i = 0; i < segments; i++) {
            const p0 = pts[closed ? (i - 1 + n) % n : Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[closed ? (i + 1) % n : Math.min(n - 1, i + 1)];
            const p3 = pts[closed ? (i + 2) % n : Math.min(n - 1, i + 2)];
            const cp1x = p1.x + (p2.x - p0.x) / 6;
            const cp1y = p1.y + (p2.y - p0.y) / 6;
            const cp2x = p2.x - (p3.x - p1.x) / 6;
            const cp2y = p2.y - (p3.y - p1.y) / 6;
            d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }
        if (closed) d += ' Z';
        return d;
    }, []);

    const annotateCurveAddPoint = useCallback((e) => {
        if (!annotateCurveMode) return;
        if (annotateCurveDragUsed.current) {
            annotateCurveDragUsed.current = false;
            return;
        }
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        if (x < 0 || x > 1 || y < 0 || y > 1) return;
        setAnnotateCurvePoints(prev => [...prev, { x, y }]);
    }, [annotateCurveMode]);

    const annotateCurveRemoveLast = useCallback(() => {
        setAnnotateCurvePoints(prev => prev.slice(0, -1));
    }, []);

    const annotateCurveApply = useCallback(() => {
        const ctx = annotateCtxRef.current;
        const canvas = annotateCanvasRef.current;
        if (!ctx || !canvas || annotateCurvePoints.length < 2) return;
        const w = canvas.width;
        const h = canvas.height;
        const pts = annotateCurvePoints.map(p => ({ x: p.x * w, y: p.y * h }));
        const n = pts.length;
        const closed = annotateCurveClosed;
        const scaleX = canvas.width / canvas.getBoundingClientRect().width;
        ctx.beginPath();
        ctx.strokeStyle = annotateBrushColor;
        ctx.lineWidth = annotateBrushSize * scaleX;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.moveTo(pts[0].x, pts[0].y);
        const segments = closed ? n : n - 1;
        for (let i = 0; i < segments; i++) {
            const p0 = pts[closed ? (i - 1 + n) % n : Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[closed ? (i + 1) % n : Math.min(n - 1, i + 1)];
            const p3 = pts[closed ? (i + 2) % n : Math.min(n - 1, i + 2)];
            const cp1x = p1.x + (p2.x - p0.x) / 6;
            const cp1y = p1.y + (p2.y - p0.y) / 6;
            const cp2x = p2.x - (p3.x - p1.x) / 6;
            const cp2y = p2.y - (p3.y - p1.y) / 6;
            ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
        }
        if (closed) ctx.closePath();
        ctx.stroke();
        pushHistory();
        setAnnotateCurvePoints([]);
    }, [annotateCurvePoints, annotateCurveClosed, annotateBrushColor, annotateBrushSize, pushHistory]);

    const save = useCallback(() => {
        const canvas = annotateCanvasRef.current;
        if (!canvas) return;
        const out = document.createElement('canvas');
        out.width = canvas.width;
        out.height = canvas.height;
        const octx = out.getContext('2d');
        octx.drawImage(canvas, 0, 0);
        if (annotateCurvePoints.length >= 2) {
            const pts = annotateCurvePoints.map(p => ({ x: p.x * out.width, y: p.y * out.height }));
            const n = pts.length;
            const closed = annotateCurveClosed;
            octx.beginPath();
            octx.strokeStyle = annotateBrushColor;
            octx.lineWidth = annotateBrushSize * (canvas.width / canvas.getBoundingClientRect().width);
            octx.lineCap = 'round';
            octx.lineJoin = 'round';
            octx.moveTo(pts[0].x, pts[0].y);
            const segments = closed ? n : n - 1;
            for (let i = 0; i < segments; i++) {
                const p0 = pts[closed ? (i - 1 + n) % n : Math.max(0, i - 1)];
                const p1 = pts[i];
                const p2 = pts[closed ? (i + 1) % n : Math.min(n - 1, i + 1)];
                const p3 = pts[closed ? (i + 2) % n : Math.min(n - 1, i + 2)];
                const cp1x = p1.x + (p2.x - p0.x) / 6;
                const cp1y = p1.y + (p2.y - p0.y) / 6;
                const cp2x = p2.x - (p3.x - p1.x) / 6;
                const cp2y = p2.y - (p3.y - p1.y) / 6;
                octx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
            }
            if (closed) octx.closePath();
            octx.stroke();
        }
        for (const note of annotateNotes) {
            if (!note.text) continue;
            const x1 = note.px * out.width;
            const y1 = note.py * out.height;
            const x2 = note.tx * out.width;
            const y2 = note.ty * out.height;
            octx.beginPath();
            octx.moveTo(x1, y1);
            octx.lineTo(x2, y2);
            octx.strokeStyle = '#fff';
            octx.lineWidth = 2;
            octx.stroke();
            octx.beginPath();
            octx.arc(x1, y1, 5, 0, Math.PI * 2);
            octx.fillStyle = '#c9a0dc';
            octx.fill();
            octx.font = `${Math.round(out.height * 0.018)}px sans-serif`;
            const metrics = octx.measureText(note.text);
            const pad = 6;
            const tw = metrics.width + pad * 2;
            const th = out.height * 0.018 + pad * 2;
            octx.fillStyle = 'rgba(0,0,0,0.7)';
            octx.roundRect(x2 - pad, y2 - th + pad, tw, th, 4);
            octx.fill();
            octx.fillStyle = '#fff';
            octx.fillText(note.text, x2, y2);
        }
        const dataUrl = out.toDataURL('image/png');
        const count = gallery.filter(g => g.label.startsWith('Annotated')).length;
        setGallery(prev => [...prev, { url: dataUrl, label: `Annotated ${count + 1}`, category: 'result' }]);
    }, [gallery, setGallery, annotateNotes, annotateCurvePoints, annotateCurveClosed, annotateBrushColor, annotateBrushSize]);

    useEffect(() => {
        if (galleryTab === 'annotate' && filteredGallery.length > 0) {
            const img = filteredGallery[selectedGalleryIndex];
            if (img) initCanvas(img.url);
            setAnnotateNotes([]);
            setAnnotateEditingNote(null);
        }
    }, [galleryTab, selectedGalleryIndex, filteredGallery, initCanvas]);

    useEffect(() => {
        const canvas = annotateCanvasRef.current;
        const container = annotateContainerRef.current;
        if (!canvas || !container || galleryTab !== 'annotate') return;
        const update = () => {
            const cRect = canvas.getBoundingClientRect();
            const pRect = container.getBoundingClientRect();
            setAnnotateCanvasRect({
                left: cRect.left - pRect.left,
                top: cRect.top - pRect.top,
                width: cRect.width,
                height: cRect.height,
            });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(canvas);
        ro.observe(container);
        return () => ro.disconnect();
    }, [galleryTab, selectedGalleryIndex, filteredGallery]);

    return {
        annotateCanvasRef,
        annotateContainerRef,
        annotateCanvasRect,
        annotateDrawing,
        annotateBrushColor, setAnnotateBrushColor,
        annotateBrushSize, setAnnotateBrushSize,
        annotateEraser, setAnnotateEraser,
        annotateZoomMode, setAnnotateZoomMode,
        drawTool, setDrawTool,
        annotateNoteMode, setAnnotateNoteMode,
        annotateNotes, setAnnotateNotes,
        annotateEditingNote, setAnnotateEditingNote,
        annotateDraggingNote,
        annotateCurveMode, setAnnotateCurveMode,
        annotateCurvePoints, setAnnotateCurvePoints,
        annotateCurveClosed, setAnnotateCurveClosed,
        annotateCurveDragging, annotateCurveDragUsed,
        addNote,
        startDraw,
        draw,
        endDraw,
        undo,
        save,
        generateCurvePath,
        annotateCurveAddPoint,
        annotateCurveRemoveLast,
        annotateCurveApply,
    };
}
